import { useEffect, useMemo, useState } from 'react';
import { memberService, userService } from '../../clients/ApiClients';
import {
  CommentReturn,
  MemberReturn,
  UserReturn,
} from '../../clients/rust-api-client';
import { useSyncedMembers, useSyncedUsers } from '../../sync/hooks';

/**
 * `author_id` -> a name to print.
 *
 * `CommentReturn` carries only an id and the realm it belongs to, so the
 * names have to be fetched. The cache is module-level rather than per
 * component: a thread is usually a handful of people talking repeatedly,
 * and switching tabs or reopening the violation should not re-ask.
 */
const names = new Map<string, string>();
const inFlight = new Map<string, Promise<string>>();

/**
 * What to print when the lookup fails or the person has been removed.
 *
 * Never the raw id: `user_01J...` in place of a name reads as a bug, and
 * on a record that gets read back in a dispute it is worse than vague.
 */
const fallbackFor = (realm: string): string =>
  realm === 'member' ? 'A homeowner' : 'A team member';

const memberName = (m: MemberReturn): string => {
  const first = m.preferred_name?.trim() || m.first_name;
  return [first, m.last_name].filter(Boolean).join(' ').trim();
};

const staffName = (u: UserReturn): string => u.full_name?.trim() || u.email;

const fetchName = async (
  authorId: string,
  realm: string
): Promise<string> => {
  if (realm === 'member') {
    const { data } = await memberService.getMember({ id: authorId });
    return memberName(data);
  }
  const { data } = await userService.getUser({ id: authorId });
  return staffName(data);
};

const resolve = (authorId: string, realm: string): Promise<string> => {
  const cached = names.get(authorId);
  if (cached) return Promise.resolve(cached);

  const pending = inFlight.get(authorId);
  if (pending) return pending;

  // Ten comments by one manager cost one request, and React's double
  // mount in development does not double it again.
  const request = fetchName(authorId, realm)
    .then((name) => name || fallbackFor(realm))
    .catch(() => fallbackFor(realm))
    .then((name) => {
      names.set(authorId, name);
      inFlight.delete(authorId);
      return name;
    });

  inFlight.set(authorId, request);
  return request;
};

/** Anyone with a name to print: a comment's author, or a violation's
 *  creator and last editor on the activity timeline. */
export type Author = Pick<CommentReturn, 'author_id' | 'author_realm'>;

/**
 * Resolve every distinct author in a thread.
 *
 * Returns a lookup rather than a list, so a comment renders whatever is
 * known at the time — a name that has not arrived yet simply falls back,
 * and no part of the thread waits on a name to paint.
 */
export const useAuthorNames = (
  comments: Author[]
): ((comment: Author) => string) => {
  const [, setVersion] = useState(0);

  // Names the sync engine's copy already holds (#561) need no request, and
  // stay current if the person is renamed. The copy has every member the
  // viewer can see, but staff only for an Owner or Admin — anyone else
  // has just themselves there, so other staff still resolve by request.
  const syncedMembers = useSyncedMembers({});
  const syncedUsers = useSyncedUsers({});
  const known = useMemo(() => {
    const out = new Map<string, string>();
    syncedMembers?.forEach((m) => out.set(`member:${m.id}`, memberName(m)));
    syncedUsers?.forEach((u) => out.set(`user:${u.id}`, staffName(u)));
    return out;
  }, [syncedMembers, syncedUsers]);
  const fromStore = (c: Author): string | undefined =>
    known.get(`${c.author_realm === 'member' ? 'member' : 'user'}:${c.author_id}`) ||
    undefined;

  const ids = comments
    .map((c) => `${c.author_id}:${c.author_realm}`)
    .sort()
    .join(',');

  useEffect(() => {
    let cancelled = false;
    const unresolved = comments.filter(
      (c) => !fromStore(c) && !names.has(c.author_id)
    );
    if (unresolved.length === 0) return;

    Promise.all(
      unresolved.map((c) => resolve(c.author_id, c.author_realm))
    ).then(() => {
      if (!cancelled) setVersion((v) => v + 1);
    });

    return () => {
      cancelled = true;
    };
    // `ids` is the stable identity of "which authors are on screen".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids, known]);

  return (comment: Author): string =>
    fromStore(comment) ??
    names.get(comment.author_id) ??
    fallbackFor(comment.author_realm);
};

/** Test seam — the cache outlives a component by design. */
export const __resetAuthorNameCache = (): void => {
  names.clear();
  inFlight.clear();
};
